import { builder } from '@builder.io/react';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { Button, ButtonColor, ButtonSize } from '@videoblocks/storywind';

import { setAudioFiltersChanged } from '../../../Audio/actions/AudioActions';
import {
  ADOBE_PLUGIN_LANDING_PAGE_RELATIVE_PATH,
  ADOBE_PLUGIN_MARKETPLACE_URL,
} from '../../../Enterprise/AdobeConstants';
import { getMonthlyDownloadsRemaining } from '../../../FeatureLimits/FeatureLimitsApi';
import { updateMonthlyDownloadsRemaining } from '../../../auth/AuthActions';
import {
  selectAuthFeatureFlags,
  selectAuthPermissions,
  selectIsLoggedIn,
  selectIsMarketingAudience,
  selectMonthlyDownloadsRemaining,
  selectPrimarySubscription,
} from '../../../auth/AuthSelectors';
import { Subscription, PlanClassification } from '../../../auth/AuthTypes';
import BrandsAndCreatorsSection from '../../../builder-io/components/blocks/BrandsAndCreatorsSection.builder';
import ExploreMusicLibrary from '../../../builder-io/components/blocks/ExploreMusicLibrary.builder';
import Faq from '../../../builder-io/components/blocks/FAQ.builder';
import FooterHookLandingSearch from '../../../builder-io/components/blocks/FooterHookLandingSearch.builder';
import RichText from '../../../builder-io/components/blocks/RichText.builder';
import { BUTTON_VARIANTS } from '../../../builder-io/utils/constants';
import SiteConstants, {
  SiteEnum,
} from '../../../common/SiteConstants/SiteConstants';
import Head from '../../../common/components/Head';
import { getCurrentSite, getUrlSlug } from '../../../common/utils';
import { searchOrigins } from '../../Navigation/constants';
import { ContentClass, SearchFilterContentTypes } from '../../Shared/enums';
import { RelatedSearch, SearchFeatures } from '../SearchTypes';
import {
  collapseMoreLikeThisDrawer,
  setIsSideMenuOpened,
  updateSearchOptionsAndRefresh,
} from '../actions/SearchActions';
import FilterButton from '../components/FilterButton';
import HumanMadeTag from '../components/HumanMadeTag';
import SortDropdown from '../components/SortDropdown';
import SubscribeBanner from '../components/SubscribeBanner';
import ResultCount from '../components/appliedFilters/ResultCount';
import MobileFiltersModal from '../components/modal/MobileFiltersModal';
import MobileSortModal from '../components/modal/MobileSortModal';
import InternalSearchLinksSEO from '../components/relatedSearch/InternalSearchLinksSEO';
import RelatedSearchContainer from '../components/relatedSearch/RelatedSearchContainer';
import { ALL_VIDEOS_CONTENT_TYPE } from '../entities/SearchFilterOptions';
import {
  appleMotionTemplateTypeId,
  templatesContentTypeLabel,
  daVinciResolveTemplateTypeId,
} from '../entities/VideoSearchFilterOptions';
import { useSearchHeroData } from '../hooks/useSearchHeroData';
import { isConversationalSearchEnabledForContentClass } from '../hooks/useSearchWithAI';
import {
  selectCanonicalLink,
  selectHideSearchResults,
  selectPagination,
  selectRelatedSearch,
  selectSearchContext,
  selectSearchDescription,
  selectSearchDescriptionWithoutSearchTerm,
  selectSearchFeatures,
  selectSearchFilterOptions,
  selectSearchIsLoading,
  selectSearchPageHeading,
  selectSearchTerm,
  selectSslid,
  selectSuggestionsQuery,
  selectUrl,
} from '../selectors/searchSelectors';
import { SelectedSearchFilterOptions } from './MenuContainerInterfaces';
import ProminentSearchContainer from './ProminentSearchContainer';
import SearchResultsContainer from './SearchResultsContainer';

import '../../../../common/stylesheets/modules/search/SearchContainer.less';
import '../../../../common/stylesheets/modules/search/search-side-menu.less';

type MenuContainer = ({
  closeSideMenu,
  showSideMenu,
  shouldOpenSideMenu,
}: {
  closeSideMenu: () => void;
  showSideMenu: () => void;
  shouldOpenSideMenu: boolean;
}) => React.ReactElement;

type Props = {
  // passed
  renderAppliedFilters: () => React.ReactNode;
  menuContainer: MenuContainer;

  // redux
  canonicalLink: string;
  searchDescription: string;
  searchTerm: string;
  searchDescriptionWithoutSearchTerm: string;
  hideSearchResults: boolean;
  searchIsLoading: boolean;
  selectedSearchFilterOptions: SelectedSearchFilterOptions;
  relatedSearch: RelatedSearch[];
  features: SearchFeatures;
  pageHeading: string;
  isLoggedIn: boolean;
  isMarketingAudience: boolean;
  pproPluginEnabled: boolean;
  primarySubscription: Subscription;
  monthlyDownloadsRemaining: number;
  context: string;
  sslid: string;
  numberOfResults: number;
  selectedSuggestionsQuery: string;
  url: string;
  showConversationalSearch: boolean;
  conversationalAnimatedToggleEnabled: boolean;
  showConversationalSearchAudio: boolean;

  // actions
  updateMonthlyDownloadsRemaining: typeof updateMonthlyDownloadsRemaining;
  setIsSideMenuOpened: typeof setIsSideMenuOpened;
  setAudioFiltersChanged: typeof setAudioFiltersChanged;
  updateSearchOptionsAndRefresh: typeof updateSearchOptionsAndRefresh;
  collapseMoreLikeThisDrawer: typeof collapseMoreLikeThisDrawer;

  calculateAppliedFilterNums: (
    selectedSearchFilterOptions: SelectedSearchFilterOptions
  ) => number;
};

type State = {
  shouldOpenSideMenu: boolean;
  errorMessage: string;
  shouldShowPProPluginDialog: boolean;
};

builder.init(__BUILDER_PUBLIC_KEY__);

/**
 * Whether the filter side menu should be open by default. It opens on desktop
 * for non-video content, when the open-filters experiment is active, or when
 * the conversational search callout is shown (so the callout is visible without
 * the user having to expand filters first). Always collapsed on mobile.
 */
export function shouldOpenSideMenuByDefault({
  features,
  contentClass,
  conversationalAnimatedToggleEnabled,
  showConversationalSearchAudio,
  isLoggedIn,
}: {
  features: SearchFeatures;
  contentClass: string;
  conversationalAnimatedToggleEnabled: boolean;
  showConversationalSearchAudio: boolean;
  isLoggedIn: boolean;
}): boolean {
  const showsConversationalSearchCallout =
    conversationalAnimatedToggleEnabled &&
    isConversationalSearchEnabledForContentClass(
      contentClass as ContentClass,
      showConversationalSearchAudio
    );

  return Boolean(
    !features.isMobile &&
      (contentClass !== ContentClass.Video ||
        !isLoggedIn ||
        showsConversationalSearchCallout)
  );
}

const seoContentTypesToFetch = [
  SearchFilterContentTypes.Footage,
  SearchFilterContentTypes.Templates,
];

type SearchSeoBannerProps = {
  url: string;
  isLoggedIn: boolean;
  isLanding: boolean;
  contentClass: string;
  contentType: SearchFilterContentTypes;
  pageHeading: string;
  searchDescriptionWithoutSearchTerm: string;
  showConversationalSearch: boolean;
};

/**
 * Header block above the search results: the SEO title/paragraph plus the search
 * bar. It's a function component (SearchContainer is a class and can't use hooks)
 * so it can read the landing-search-page data via useSearchHeroData. That data
 * drives `showLandingHero`: if it's complete, render the LandingSearchHero and
 * drop the SEO headers; if not, keep the classic search bar + SEO headers.
 */
function SearchSeoBanner({
  url,
  isLoggedIn,
  isLanding,
  contentClass,
  contentType,
  pageHeading,
  searchDescriptionWithoutSearchTerm,
  showConversationalSearch,
}: SearchSeoBannerProps) {
  const urlSlug = getUrlSlug(url);

  // Only fetch on landing pages (the sole place the hero can appear). The hook
  // no-ops when disabled, so regular search pages fire no extra Builder request.
  const { builderData, isLoading } = useSearchHeroData(urlSlug, isLanding);

  // Flag used below to pick the hero vs. the classic fallback. Builder gaps are
  // filled with the design defaults, so this is false only on a landing slug we
  // have no design content for at all.
  const showLandingHero = isLanding && (isLoading || builderData !== null);

  // Hide SEO headers when logged in or when the hero takes over (it renders its
  // own heading). Otherwise show them — including the landing fallback, which
  // renders like a normal non-landing search page.
  const optionalSeoHeaders =
    isLoggedIn || showLandingHero ? null : contentClass === 'video' &&
      seoContentTypesToFetch.includes(contentType) ? (
      // Fetch content with fallback content
      <RichText
        urlSlug={urlSlug}
        modelName="category-search-page"
        altHeader={pageHeading}
        altContent={searchDescriptionWithoutSearchTerm}
      />
    ) : (
      <RichText
        header={pageHeading}
        content={searchDescriptionWithoutSearchTerm}
      />
    );

  return (
    <div
      className={twMerge(
        'm-auto mb-9 flex flex-col gap-y-6 items-center',
        !showLandingHero && 'max-w-3xl',
        isLanding && !isLoading && builderData === null && 'mt-12'
      )}
    >
      {optionalSeoHeaders}
      <div
        id="unifiedNav-search-results"
        data-testid="unifiedNav-search-results"
        className={twMerge(showLandingHero && 'w-full')}
      >
        <ProminentSearchContainer
          showContentToggles={false}
          useNewSearchUI
          showConversationalSearchCta={showConversationalSearch}
          isLanding={showLandingHero}
          builderData={builderData}
          heroDataIsLoading={isLoading}
        />
      </div>
    </div>
  );
}

class SearchContainer extends Component<Props, State> {
  constructor(props) {
    let showPProDialog = false; // don't default to true because react reconciliation will do weird things
    if (window.localStorage) {
      showPProDialog = localStorage.getItem('showPProPluginDialog') === null;
    }
    super(props);

    this.state = {
      shouldOpenSideMenu: shouldOpenSideMenuByDefault({
        features: props.features,
        contentClass: props.selectedSearchFilterOptions.contentClass,
        conversationalAnimatedToggleEnabled:
          props.conversationalAnimatedToggleEnabled,
        showConversationalSearchAudio: props.showConversationalSearchAudio,
        isLoggedIn: this.props.isLoggedIn,
      }),
      errorMessage: '',
      shouldShowPProPluginDialog: showPProDialog,
    };

    // for hotjar exit survey experiment
    const myWindow = window as any;
    if (this.props.isLoggedIn) {
      myWindow.hj = () => {};
    }
  }

  async componentDidMount() {
    /**
     * Because the Search page is server-side rendered, the browser will cache the
     * previous version of the javascript bundle, including the redux state (ex. 10 downloads remaining).
     * This call specifically addresses the edge-case where a user:
     *  - downloads something on the Search page (i.e. now 9 downloads remaining)
     *  - navigates away to another page
     *  - and then clicks the browser back button to come back to the Search page.
     *
     * It will update the "downloads remaining" counter that is shown so
     * it's not stale (i.e. '9' instead of '10')
     */
    if (
      !this.props.primarySubscription?.isMakerOnly &&
      this.props.primarySubscription?.planClassification ===
        PlanClassification.STARTER_PLAN_CLASSIFICATION
    ) {
      try {
        const monthlyDownloadsRemaining = await getMonthlyDownloadsRemaining();
        this.props.updateMonthlyDownloadsRemaining(monthlyDownloadsRemaining);
      } catch (e) {
        console.error(
          'There was an error getting monthly downloads remaining:',
          e
        );
      }
    }
  }

  closePremiereProPluginDialog() {
    this.setState({ shouldShowPProPluginDialog: false });
    if (window.localStorage) {
      localStorage.setItem('showPProPluginDialog', 'false');
    }
  }

  showSideMenu() {
    this.setState({ shouldOpenSideMenu: true });
    this.props.setIsSideMenuOpened(true);
  }

  closeSideMenu() {
    this.setState({ shouldOpenSideMenu: false });
    this.props.setIsSideMenuOpened(false);
  }
  getMenuContainer() {
    const { menuContainer: MenuContainer } = this.props;

    return (
      <>
        <div className={`${this.state.shouldOpenSideMenu && 'ml-4'}`}>
          <MenuContainer
            closeSideMenu={this.closeSideMenu.bind(this)}
            showSideMenu={this.showSideMenu.bind(this)}
            shouldOpenSideMenu={this.state.shouldOpenSideMenu}
          />
        </div>
        <MobileFiltersModal />
        <MobileSortModal />
      </>
    );
  }

  shouldHideResultCount() {
    const { searchIsLoading, selectedSearchFilterOptions } = this.props;

    if (searchIsLoading || !selectedSearchFilterOptions) {
      return true;
    } else {
      const {
        contentType,
        searchTerm,
        minDuration,
        maxDuration,
        talentReleased,
        propertyReleased,
      } = selectedSearchFilterOptions;

      return (
        contentType === ALL_VIDEOS_CONTENT_TYPE &&
        !searchTerm &&
        minDuration === SiteConstants.getInstance().getMinDuration() &&
        maxDuration === SiteConstants.getInstance().getMaxDuration() &&
        !talentReleased &&
        !propertyReleased
      );
    }
  }
  renderSearchTopBar() {
    const { selectedSearchFilterOptions } = this.props;
    const { sort, sortOrder } = selectedSearchFilterOptions;
    // reconstructs ordered sort options into a valid sort option key
    const filterValue = sortOrder ? `${sort}_${sortOrder.toLowerCase()}` : sort;
    const numFilters = this.props.calculateAppliedFilterNums(
      selectedSearchFilterOptions
    );
    const isVideoContent =
      selectedSearchFilterOptions.contentType ===
        SearchFilterContentTypes.All_videos_content_type ||
      selectedSearchFilterOptions.contentType ===
        SearchFilterContentTypes.Footage;

    return (
      <div>
        <div className="flex w-full flex-wrap items-center gap-y-2 p-4">
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <FilterButton
              isOpen={this.state.shouldOpenSideMenu}
              closeSideMenu={this.closeSideMenu.bind(this)}
              openSideMenu={this.showSideMenu.bind(this)}
              numFilters={numFilters}
              showNewBadge={isVideoContent}
            />
            <SortDropdown
              value={filterValue}
              options={this.getSortOptionsNew()}
              onChange={(value) => {
                this.handleSortChanged('sort', value);
              }}
            />
            {!this.props.features.showHumanMadeContent &&
              this.renderResultCountsForVideo()}
          </div>
          {this.props.features.showHumanMadeContent && (
            // Wraps onto its own line below the filters/sort row when the bar
            // is too narrow (~<800px); count wraps under the tag at very small widths.
            <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1">
              <HumanMadeTag />
              {/* count rendered in normal flow so the tag sits to its left */}
              {this.renderResultCountsForVideo('self-center')}
            </div>
          )}
        </div>
        {this.getRelatedSearchContainer()}
      </div>
    );
  }

  renderResultCountsForVideo(className = 'absolute right-0 pr-4 -z-1') {
    const isVideo =
      this.props.selectedSearchFilterOptions.contentClass === 'video';
    if (isVideo) {
      const { numberOfResults } = this.props;
      if (this.shouldHideResultCount()) {
        return null;
      } else {
        return (
          <ResultCount
            numberOfResults={numberOfResults}
            className={className}
          />
        );
      }
    }
  }
  renderSearchHTMLHeadElements() {
    // Usually meta data is handled on the BE,
    // but the canonical link should be handled client side in case crawlers access any client side searches.
    // In that case we want to show the correct canonical to reference in SERPs

    const { canonicalLink, numberOfResults } = this.props;
    return (
      <Head>
        {canonicalLink && <link rel="canonical" href={canonicalLink} />}
        {numberOfResults && (
          <script type="application/ld+json">
            {`{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "numberOfItems": "${numberOfResults}"
        }`}
          </script>
        )}
      </Head>
    );
  }

  render() {
    const searchResultClass = this.state.shouldOpenSideMenu
      ? `open-side-menu ${getCurrentSite()}`
      : `close-side-menu ${getCurrentSite()}`;

    // No `w-full` here: Tailwind compiles it with !important, which overrides the
    // `width: calc(100vw - sidebar widths)` rule in SearchContainer.less and makes the
    // results column overflow the viewport when the filter sidebar is open. `flex-grow: 1`
    // already stretches the column to full width when the sidebar is closed.
    const remainingSearchUI = 'flex flex-col min-w-0 px-4 pb-4';

    return (
      <>
        {this.renderSearchHTMLHeadElements()}
        <div id="search-results-app" className="SearchContainer">
          {this.renderSeoBanner()}
          {this.renderSearchTopBar()}
          <div className="search-results-app-container">
            {this.getMenuContainer()}
            <div
              id="main-search-content"
              className={`searchContainer-results ${searchResultClass} ${remainingSearchUI}`}
            >
              {this.renderMainSearchContentControl()}
            </div>
          </div>
          {this.props.isMarketingAudience && (
            <>
              {this.renderBrandsAndCreators()}
              {this.renderExploreMusicLibrary()}
              {this.renderFooterHook()}
              {!this.props.searchIsLoading && this.renderFaq()}
            </>
          )}
        </div>
      </>
    );
  }

  isLanding() {
    const landingRoutes = ['/video/search', '/images/search', '/audio/search'];
    const { url, searchTerm } = this.props;
    return landingRoutes.some((route) => url === route) && !searchTerm;
  }

  renderBrandsAndCreators() {
    const { url } = this.props;
    const urlSlug = getUrlSlug(url);
    const isLanding = this.isLanding();
    return isLanding ? <BrandsAndCreatorsSection urlSlug={urlSlug} /> : null;
  }

  renderExploreMusicLibrary() {
    const { url } = this.props;
    const urlSlug = getUrlSlug(url);
    const isLanding = this.isLanding();
    return isLanding && urlSlug === '/audio' ? (
      <ExploreMusicLibrary
        urlSlug={urlSlug}
        shouldOpenSideMenu={this.state.shouldOpenSideMenu}
      />
    ) : null;
  }

  renderFooterHook() {
    const { url } = this.props;
    const urlSlug = getUrlSlug(url);
    const isLanding = this.isLanding();
    return isLanding ? (
      <FooterHookLandingSearch
        urlSlug={urlSlug}
        modelName="landing-search-page"
        shadowVariant={false}
        showCallToAction={true}
        callToAction={{
          text: 'Explore Plans',
          href: '/pricing',
          variant: BUTTON_VARIANTS[5],
          size: 'default',
          target: '_self',
          rel: '',
        }}
      />
    ) : null;
  }

  renderFaq() {
    const { url, selectedSearchFilterOptions } = this.props;
    const { contentClass, contentType } = selectedSearchFilterOptions;
    const urlSlug = getUrlSlug(url);

    // Allowed content types to fetch content from Builder Model
    const contentTypesToFetch = [
      SearchFilterContentTypes.Footage,
      SearchFilterContentTypes.Templates,
    ];

    const isLanding = this.isLanding();
    if (isLanding) {
      return (
        <Faq
          urlSlug={urlSlug}
          modelName="landing-search-page"
          collapseItems={true}
        />
      );
    } else if (
      contentClass === 'video' &&
      contentTypesToFetch.includes(contentType)
    ) {
      return <Faq urlSlug={urlSlug} modelName="category-search-page" />;
    } else {
      return null;
    }
  }

  renderAdditionalRelatedSearchTerm = () => {
    const { relatedSearch } = this.props;
    const maxNumberOfRelatedSearchTerms = 25;

    return (
      relatedSearch &&
      relatedSearch.length > maxNumberOfRelatedSearchTerms && (
        <div className="flex flex-col space-y-2 mb-12">
          <span className="font-bold text-gray-900 text-lg">
            Users also searched for:{' '}
          </span>
          <div className="flex space-x-1">
            {relatedSearch
              .slice(maxNumberOfRelatedSearchTerms)
              .map((el, index) => {
                return (
                  <div key={index}>
                    <a className="font-semibold text-sm" href={el.url}>
                      {el.relatedSearchTerm}
                    </a>
                    {index < maxNumberOfRelatedSearchTerms - 1 && (
                      <span>{', '}</span>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )
    );
  };

  renderSeoBanner = () => {
    const {
      url,
      isLoggedIn,
      selectedSearchFilterOptions,
      pageHeading,
      searchDescriptionWithoutSearchTerm,
      showConversationalSearch,
    } = this.props;
    const { contentClass, contentType } = selectedSearchFilterOptions;

    // Delegated to a function component so it can fetch the hero data via the
    // useSearchHeroData hook (a class component can't) and decide, in one place,
    // between the landing hero and the classic search-bar + SEO-headers fallback.
    return (
      <SearchSeoBanner
        url={url}
        isLoggedIn={isLoggedIn}
        isLanding={this.isLanding()}
        contentClass={contentClass}
        contentType={contentType}
        pageHeading={pageHeading}
        searchDescriptionWithoutSearchTerm={searchDescriptionWithoutSearchTerm}
        showConversationalSearch={showConversationalSearch}
      />
    );
  };

  renderMainSearchContentControl() {
    const { primarySubscription, selectedSearchFilterOptions } = this.props;
    const { contentType, templateType } = selectedSearchFilterOptions;

    return (
      <div className="w-full flex flex-col">
        <div id="searchContainer-adobeBanner">
          {contentType === templatesContentTypeLabel &&
            !this.props.features.isMobile &&
            this.state.shouldShowPProPluginDialog &&
            templateType !== appleMotionTemplateTypeId &&
            templateType !== daVinciResolveTemplateTypeId &&
            this.props.isLoggedIn &&
            this.renderAdobePluginDialogToMembers()}
        </div>
        {this.renderSuggestionsCorrectionText()}
        <div
          className="searchContainer-appliedFilters z-5"
          id="searchContainer-filters"
        >
          {this.props.renderAppliedFilters()}
        </div>
        {!this.props.primarySubscription?.isMakerOnly &&
          this.props.primarySubscription?.planClassification ===
            PlanClassification.STARTER_PLAN_CLASSIFICATION &&
          Number.isInteger(primarySubscription.monthlyDownloadsRemaining) && (
            <div className="flex-shrink-0 text-sm font-bold text-gray-800">
              {primarySubscription.monthlyDownloadsRemaining} downloads left
              this month
            </div>
          )}
        <SearchResultsContainer />
        {!this.props.searchIsLoading &&
          this.renderAdditionalRelatedSearchTerm()}
        {!this.shouldShowRelatedSearch() && <InternalSearchLinksSEO />}
        {/* Last child so position: sticky reserves space at the end and floats above the
            results while scrolling (RFPC-1342). */}
        {this.props.features.showSubscribeBanner && <SubscribeBanner />}
      </div>
    );
  }

  renderAdobePluginDialogToMembers() {
    const adobeUrl = this.props.pproPluginEnabled
      ? ADOBE_PLUGIN_MARKETPLACE_URL
      : ADOBE_PLUGIN_LANDING_PAGE_RELATIVE_PATH;
    return (
      <aside className="py-3 px-4 mb-6 bg-gray-200 w-full rounded flex justify-between items-start">
        <div className="flex">
          <img
            src="/assets/common/images/home-v2/icons/ppro-circle.png"
            alt="Premiere Pro Icon"
            className="mr-3 w-9 h-9"
          />
          <img
            src="/assets/common/images/home-v2/icons/ae-circle.png"
            alt="After Effects Icon"
            className="mr-3 w-9 h-9"
          />
          <a
            target="_blank"
            href={adobeUrl}
            className="leading-5 font-semibold text-base text-black flex items-center min-h-9"
          >
            Search Storyblocks' library and download directly in your editor
            with our new plugin for Adobe Creative Cloud.
          </a>
        </div>
        <div className="flex ml-4">
          <Button
            href={adobeUrl}
            target="_blank"
            color={ButtonColor.Secondary}
            size={ButtonSize.Small}
            className="whitespace-nowrap min-h-9 flex items-center"
            onClick={() =>
              Telemetry.increment('ppro.site-hook.ppro-template-search-banner')
            }
          >
            Learn More &#8599;
          </Button>
          <div
            role="button"
            tabIndex={0}
            className="ml-4 cursor-pointer"
            onKeyDown={() => this.closePremiereProPluginDialog()}
            onClick={() => this.closePremiereProPluginDialog()}
          >
            <img
              className="min-w-6 min-h-8"
              src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/sb-close-black.svg`}
              alt="Close"
              width={24}
              height={24}
            />
          </div>
        </div>
      </aside>
    );
  }

  renderPProPluginDialogToMembers() {
    return (
      <div className="py-3 px-4 mb-6 bg-gray-200 w-full rounded">
        <div className="flex justify-between items-start">
          <div className="flex">
            <img
              src="/assets/common/images/home-v2/icons/ppro-circle.png"
              alt="Premiere Pro Icon"
              className="mr-3 w-9 h-9"
            />
            <a
              target="_blank"
              href={ADOBE_PLUGIN_MARKETPLACE_URL}
              className="leading-5 font-semibold text-base text-black flex items-center min-h-9"
            >
              Search Storyblocks' library and download directly in Premiere Pro
              with our new plugin
            </a>
          </div>
          <div className="flex ml-4">
            <Button
              href={ADOBE_PLUGIN_MARKETPLACE_URL}
              target="_blank"
              color={ButtonColor.Secondary}
              size={ButtonSize.Small}
              className="whitespace-nowrap min-h-9 flex items-center"
              onClick={() =>
                Telemetry.increment(
                  'ppro.site-hook.ppro-template-search-banner'
                )
              }
            >
              Install Now &#8599;
            </Button>
            <div
              role="button"
              tabIndex={0}
              className="ml-4 cursor-pointer"
              onKeyDown={() => this.closePremiereProPluginDialog()}
              onClick={() => this.closePremiereProPluginDialog()}
            >
              <img
                className="min-w-6 min-h-8"
                src={`${__ASSETS_COMMON_IMAGES_URL__}/icons/sb-close-black.svg`}
                alt="Close"
                width={24}
                height={24}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderSuggestionsCorrectionText() {
    const { searchTerm } = this.props.selectedSearchFilterOptions;
    const suggestionsQuery = this.props.selectedSuggestionsQuery;
    return (
      suggestionsQuery && (
        <div className="w-full text-sm font-normal text-gray-700 mb-3">
          Your search for <span className="font-bold">{searchTerm}</span> did
          not match any results. Showing results for{' '}
          <span className="font-bold">{suggestionsQuery}</span> instead
        </div>
      )
    );
  }

  getSortOptions() {
    const defaultOptions = {
      most_relevant: 'Most Relevant',
      most_downloaded: 'Most Downloaded',
      most_recent: 'Most Recent',
      trending_now: 'Trending Now',
      undiscovered: 'Undiscovered',
    };

    const audioOptions = {
      duration_asc: 'Duration: Low-High',
      duration_desc: 'Duration: High-Low',
      title_asc: 'Title: A-Z',
      title_desc: 'Title: Z-A',
    };

    return getCurrentSite() === SiteEnum.Audioblocks
      ? { ...defaultOptions, ...audioOptions }
      : defaultOptions;
  }

  getSortOptionsNew() {
    const defaultOptions = [
      {
        label: 'Most Relevant',
        value: 'most_relevant',
      },
      {
        label: 'Most Downloaded',
        value: 'most_downloaded',
      },
      {
        label: 'Most Recent',
        value: 'most_recent',
      },
      {
        label: 'Trending Now',
        value: 'trending_now',
      },
      {
        label: 'Undiscovered',
        value: 'undiscovered',
      },
    ];

    const audioOptions = [
      {
        label: 'Duration: Low-High',
        value: 'duration_asc',
      },
      {
        label: 'Duration: High-Low',
        value: 'duration_desc',
      },
      {
        label: 'Title: A-Z',
        value: 'title_asc',
      },
      {
        label: 'Title: Z-A',
        value: 'title_desc',
      },
    ];

    return getCurrentSite() === SiteEnum.Audioblocks
      ? [...defaultOptions, ...audioOptions]
      : defaultOptions;
  }

  /**
   * Extracts the sort type and sort order from ordered sort option keys
   *
   * Ordered sort options are keyed in the dropdown as SORT-TYPE_ORDER.
   * This method extracts the proper sort type and sort order, returning
   * them as a formatted tuple.
   *
   * If not an ordered sort option (i.e., keyed by SORT-TYPE), the
   * original sort type value and a sort order of null are returned.
   *
   * @param sortType
   * @returns {string[]}
   */
  getSearchApiTypeAndOrderFromSortOption(sortType = '') {
    const _idx = sortType.lastIndexOf('_');
    const sortOrder = sortType.slice(_idx + 1);

    switch (sortOrder) {
      case 'asc':
      case 'desc':
        const type = sortType.slice(0, _idx);
        return [type, sortOrder.toUpperCase()];
      default:
        return [sortType, null];
    }
  }

  handleSortChanged = (name, value) => {
    const [sortType, order] =
      this.getSearchApiTypeAndOrderFromSortOption(value);

    const updateFields = {
      [name]: sortType,
      sortOrder: order,
      page: 1, // Go back to page 1 when any filter is changed
      isPagination: false,
      searchOrigin: searchOrigins.SORT_BY,
    };

    const newSearchOptions =
      this.props.selectedSearchFilterOptions.update(updateFields);

    this.props.setAudioFiltersChanged(true);
    this.props.updateSearchOptionsAndRefresh(newSearchOptions);
    this.props.collapseMoreLikeThisDrawer();
  };

  getIsDisabled() {
    return this.props.selectedSearchFilterOptions.similarTo > 0;
  }

  shouldShowRelatedSearch() {
    const { contentType } = this.props.selectedSearchFilterOptions;

    return (
      !this.props.searchIsLoading &&
      this.props.relatedSearch &&
      this.props.relatedSearch.length > 0 &&
      contentType !== SearchFilterContentTypes.Templates &&
      contentType !== SearchFilterContentTypes.Motion_bgs
    );
  }

  getRelatedSearchContainer() {
    if (!this.shouldShowRelatedSearch()) {
      return null;
    }

    return <RelatedSearchContainer />;
  }
}

function mapStateToProps(state) {
  const pagination = selectPagination(state);
  const { pproPluginEnabled } = selectAuthPermissions(state);
  const {
    showConversationalSearch,
    conversationalAnimatedToggleEnabled,
    showConversationalSearchAudio,
  } = selectAuthFeatureFlags(state);

  return {
    canonicalLink: selectCanonicalLink(state),
    searchDescription: selectSearchDescription(state),
    searchTerm: selectSearchTerm(state),
    searchDescriptionWithoutSearchTerm:
      selectSearchDescriptionWithoutSearchTerm(state),
    hideSearchResults: selectHideSearchResults(state),
    searchIsLoading: selectSearchIsLoading(state),
    selectedSearchFilterOptions: selectSearchFilterOptions(state),
    relatedSearch: selectRelatedSearch(state),
    features: selectSearchFeatures(state),
    pageHeading: selectSearchPageHeading(state),
    isLoggedIn: selectIsLoggedIn(state),
    isMarketingAudience: selectIsMarketingAudience(state),
    pproPluginEnabled,
    primarySubscription: selectPrimarySubscription(state),
    monthlyDownloadsRemaining: selectMonthlyDownloadsRemaining(state),
    context: selectSearchContext(state),
    sslid: selectSslid(state),
    numberOfResults: pagination.totalResults,
    selectedSuggestionsQuery: selectSuggestionsQuery(state),
    url: selectUrl(state),
    showConversationalSearch,
    conversationalAnimatedToggleEnabled,
    showConversationalSearchAudio,
  };
}

export default connect(mapStateToProps, {
  updateMonthlyDownloadsRemaining,
  setIsSideMenuOpened,
  setAudioFiltersChanged,
  updateSearchOptionsAndRefresh,
  collapseMoreLikeThisDrawer,
  // @ts-ignore
})(SearchContainer);
